<template>
    <Modal
        v-model:open="modelValue"
        size="wrapper"
        fullscreen
        desktop-full-height
        scrollable-header
        no-footer
        :title="$t('pages.product_detail_page.product_info.modals.product_options_modal.title')"
    >
        <div class="flex flex-col">
            <div class="flex-1 space-y-4 px-3 pb-3">
                <ProductOptionCategory
                    v-for="productOptionCategory in productOptionsStore.productOptions"
                    :key="`product_option_category_${productOptionCategory.joost_id}`"
                    :product-option-category="productOptionCategory"
                />
            </div>

            <!-- Overview -->
            <ProductOptionsModalOverview @close="closeModal" />
        </div>
    </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/modals/Modal.vue';
import ProductOptionCategory from '@/components/product-options/ProductOptionCategory.vue';
import ProductOptionsModalOverview from '@/pages/product-page/partials/product-info/partials/modals/ProductOptionsModalOverview.vue';
import { useProductOptionsStore } from '@/stores/product-options/ProductOptionsStore';
import { useProductStore } from '@/stores/products/ProductStore';
import type { ConfigurableProductProductObject, ProductOptionObject } from '@/types/generated';
import ProductOptionsController from '@/types/wayfinder/actions/App/Http/Controllers/Api/Products/ProductOptionsController';
import { useHttp } from '@inertiajs/vue3';
import { watch } from 'vue';

const productStore = useProductStore();
const productOptionsStore = useProductOptionsStore();
const http = useHttp();

const modelValue = defineModel<boolean>('open');

watch(modelValue, isOpen => {
    if (isOpen) {
        productOptionsStore.selectDefaultOptions();
    }
});

watch(
    () => productStore.selectedProduct,
    (selectedProduct: ConfigurableProductProductObject) => {
        if (!selectedProduct?.product?.sku) {
            return;
        }

        http.get(
            ProductOptionsController({
                configurableProduct: productStore.configurableProduct.group_identifier,
                sku: selectedProduct.product.sku,
            }).url,
            {
                onSuccess: (response: unknown) => {
                    const { product_options } = response as { product_options: ProductOptionObject[] };

                    if (productOptionsStore.currentProductSku !== selectedProduct.product.sku) {
                        productOptionsStore.resetSelections();
                    }

                    productOptionsStore.currentProductSku = selectedProduct.product.sku;
                    productOptionsStore.productOptions = product_options;
                    productOptionsStore.selectDefaultOptions();
                },
                onError: () => {
                    productOptionsStore.productOptions = [];
                },
            },
        ).catch(() => {
            productOptionsStore.productOptions = [];
        });
    },
);

const closeModal = () => {
    modelValue.value = false;
};
</script>
